import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import API from '../../api';
import { toast } from 'react-toastify';
import { FiCamera } from 'react-icons/fi';

const ProfileTab = () => {
  const { user, setUser } = useAuth();
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    gender: '',
    birthday: '',
  });
  const [avatarPreview, setAvatarPreview] = useState('');
  const [avatarFile, setAvatarFile] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (user) {
      setFormData({
        name: user.name || '',
        phone: user.phone || '',
        gender: user.gender || '',
        birthday: user.birthday ? new Date(user.birthday).toISOString().split('T')[0] : '',
      });
      setAvatarPreview(user.avatar || '');
    }
  }, [user]);

  const handleChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleAvatarChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setAvatarFile(file);
      const reader = new FileReader();
      reader.onloadend = () => setAvatarPreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const data = new FormData();
      data.append('name', formData.name);
      data.append('phone', formData.phone);
      data.append('gender', formData.gender);
      if (formData.birthday) data.append('birthday', formData.birthday);
      if (avatarFile) data.append('avatar', avatarFile);

      const res = await API.put('/api/auth/profile', data, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      
      toast.success('Profile updated successfully!');
      
      // Update auth context and storage
      setUser(res.data.user);
      const storage = localStorage.getItem('poshe_token') ? localStorage : sessionStorage;
      storage.setItem('poshe_user', JSON.stringify(res.data.user));
    } catch (err) {
      toast.error('Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-6 md:p-8">
      <h2 className="font-playfair text-2xl font-bold text-navy mb-6">Profile Settings</h2>
      
      <form onSubmit={handleSubmit} className="max-w-2xl space-y-6">
        
        {/* Avatar Upload */}
        <div className="flex items-center gap-6">
          <div className="relative w-24 h-24 rounded-full border-4 border-white shadow-md bg-gray-100 flex items-center justify-center overflow-hidden">
            {avatarPreview ? (
              <img src={avatarPreview} alt="Avatar" className="w-full h-full object-cover" />
            ) : (
              <span className="text-3xl text-gray-400 font-playfair">{user?.name?.charAt(0).toUpperCase()}</span>
            )}
            <label className="absolute bottom-0 w-full h-8 bg-black/50 hover:bg-black/70 cursor-pointer flex items-center justify-center transition-colors">
              <FiCamera className="text-white" size={14} />
              <input type="file" accept="image/*" onChange={handleAvatarChange} className="hidden" />
            </label>
          </div>
          <div>
            <h3 className="font-semibold text-charcoal">{user?.name}</h3>
            <p className="text-sm text-gray-500">{user?.email}</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
            <input type="text" name="name" value={formData.name} onChange={handleChange} required className="input-field" />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email <span className="text-gray-400 font-normal">(Read Only)</span></label>
            <input type="email" value={user?.email || ''} readOnly className="input-field bg-gray-50 text-gray-500 cursor-not-allowed" />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
            <input type="tel" name="phone" value={formData.phone} onChange={handleChange} className="input-field" placeholder="+91 XXXXX XXXXX" />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Date of Birth</label>
            <input type="date" name="birthday" value={formData.birthday} onChange={handleChange} className="input-field" />
            <p className="text-xs text-gray-400 mt-1">We'll send you a special birthday treat!</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Gender</label>
            <select name="gender" value={formData.gender} onChange={handleChange} className="input-field bg-white">
              <option value="">Select Gender</option>
              <option value="male">Male</option>
              <option value="female">Female</option>
              <option value="other">Other</option>
              <option value="prefer not to say">Prefer not to say</option>
            </select>
          </div>
        </div>

        <div className="pt-4 border-t">
          <button type="submit" disabled={saving} className="btn-navy px-8 disabled:opacity-50">
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>

      </form>
    </div>
  );
};

export default ProfileTab;
